import { useEffect, useState, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { MailDomain } from '../types/models/MailDomain';
import type { ApiResponseMailDomainSingle } from '../types/api/ApiResponseMailDomain';

interface UseMailDomainReturn {
  mailDomain: MailDomain | null;
  loading: boolean;
  error: string | null;
  notFound: boolean;
  refetch: () => Promise<void>;
}

export const useMailDomain = (domainId: number): UseMailDomainReturn => {
  const [mailDomain, setMailDomain] = useState<MailDomain | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchMailDomain = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setNotFound(false);

      const response = await apiFetch(`/emails/domains/${domainId}`);

      if (response.status === 404) {
        setNotFound(true);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseMailDomainSingle;
      if (data.status === 'success' && data.data?.mail_domain) {
        setMailDomain(data.data.mail_domain);
      } else if (data.status === 'fail' || data.status === 'error') {
        setError(data.message ?? 'Failed to load mail domain');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching the mail domain');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, domainId]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchMailDomain();
    }
  }, [auth.isAuthenticated, fetchMailDomain]);

  return { mailDomain, loading, error, notFound, refetch: fetchMailDomain };
};
